import { useCallback, useMemo } from 'react'

import { FixedBanner } from '@circlefin/components'
import { useForm } from '@circlefin/form'
import { TestnetChain } from '@features/cpn.script/lib'
import * as y from 'yup'

import { SIGNING_METHODS, DEFAULT_CLASSES } from '../constants'

import type { SigningMethod } from '@features/cpn.hooks/useWalletValidator'

interface SupportedChain {
  value: TestnetChain
  label: string
  blockchain: string
}

/**
 * Get supported testnet chains for display in UI.
 */
function getSupportedChains(): SupportedChain[] {
  return [
    {
      value: TestnetChain.EthereumSepolia,
      label: 'Ethereum Sepolia',
      blockchain: 'Ethereum',
    },
    {
      value: TestnetChain.PolygonAmoy,
      label: 'Polygon Amoy',
      blockchain: 'Polygon',
    },
    {
      value: TestnetChain.BaseSepolia,
      label: 'Base Sepolia',
      blockchain: 'Base',
    },
    {
      value: TestnetChain.SolanaDevnet,
      label: 'Solana Devnet',
      blockchain: 'Solana',
    },
  ]
}

// Component-specific types
interface ParameterForm {
  chain: string
  walletAddress: string
}

interface ParameterInputProps {
  selectedMethod: SigningMethod
  onParametersSubmit: (data: ParameterForm) => void
  onBack: () => void
  error?: string | null
}

// Form validation schema
const parameterSchema = y.object({
  chain: y.string().required('Please select a chain'),
  walletAddress: y.string().required('Please enter a wallet address'),
})

/**
 * Parameter input component for the second step of wallet compatibility testing
 * Allows users to select a blockchain and enter their wallet address.
 */
export const ParameterInput: React.FC<ParameterInputProps> = ({
  selectedMethod,
  onParametersSubmit,
  onBack,
  error,
}) => {
  // Parameters Form
  const [ParameterForm] = useForm<ParameterForm>({
    schema: parameterSchema,
  })

  // Get available chains for selected method
  const availableChains = useMemo(() => {
    const methodConfig = SIGNING_METHODS.find((m) => m.value === selectedMethod)
    if (!methodConfig) return []

    return getSupportedChains()
      .filter((chain: SupportedChain) =>
        methodConfig.chains.includes(chain.value),
      )
      .map((chain: SupportedChain) => ({
        value: chain.value,
        label: chain.label,
      }))
  }, [selectedMethod])

  // Get method configuration for display
  const methodConfig = useMemo(
    () => SIGNING_METHODS.find((m) => m.value === selectedMethod),
    [selectedMethod],
  )

  // Handle parameter submission
  const handleParametersSubmit = useCallback(
    (data: ParameterForm) => {
      onParametersSubmit(data)
    },
    [onParametersSubmit],
  )

  // Get placeholder text based on selected method
  const getWalletPlaceholder = () => {
    if (selectedMethod === 'solana') {
      return 'Enter Solana wallet address (Base58)'
    }
    return 'Enter EVM wallet address (0x...)'
  }

  return (
    <div className="space-y-6">
      <div>
        <h4 className="mb-2 text-neutral-strong type-header-xs">
          Generate {methodConfig?.label} Payload
        </h4>
        <p className="mb-4 text-neutral-subtle type-body-sm">
          Enter your wallet details to generate a test payload. Always verify
          the payload before signing.
        </p>
      </div>

      <ParameterForm
        className="space-y-4"
        data-testid="parameter-form"
        onSubmit={handleParametersSubmit}
      >
        <ParameterForm.Dropdown
          data-testid="chain-dropdown"
          items={availableChains}
          label="Chain"
          name="chain"
          placeholder="Select a chain"
          required
        />

        <ParameterForm.Input
          data-testid="wallet-address-input"
          label="Wallet Address"
          name="walletAddress"
          placeholder={getWalletPlaceholder()}
          required
        />

        {error && (
          <FixedBanner
            data-testid="error-banner"
            level="page"
            status="error"
            multiline
            visible
          >
            <FixedBanner.Description>{error}</FixedBanner.Description>
          </FixedBanner>
        )}

        <div className="flex space-x-3">
          <ParameterForm.SubmitButton
            className="flex-1 sm:flex-none"
            data-testid="generate-button"
            variant="primary"
          >
            Generate Payload
          </ParameterForm.SubmitButton>
          <button
            className={DEFAULT_CLASSES.button.secondary}
            data-testid="back-button"
            onClick={onBack}
            type="button"
          >
            Back
          </button>
        </div>
      </ParameterForm>
    </div>
  )
}
